import { Component, DestroyRef, ElementRef, inject, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { RewardsWalletService, RewardsWalletQuery, WalletGroupItem, toNumber } from '@fgb/core';
import { ScreenType } from '@fgb/portal-component-library/src/lib/directives';
import {
  Observable,
  distinctUntilChanged,
  exhaustMap,
  filter,
  firstValueFrom,
  fromEvent,
  map,
  merge,
  startWith,
  takeUntil,
  tap,
} from 'rxjs';
import gsap from 'gsap';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'fgb-wallet-page',
  templateUrl: './wallet-page.component.html',
  styleUrls: ['./wallet-page.component.scss'],
})
export class WalletPageComponent implements OnInit, OnDestroy {
  walletItems$: Observable<WalletGroupItem[]>;
  screenType = ScreenType;
  private maxTranslateY = 320;
  private minTranslateY = 50;
  isDragging = false;
  translateY = 0;
  @ViewChild('draggableY', { static: true }) draggable!: ElementRef<HTMLDivElement>;
  @ViewChild('draggableBody', { static: true }) draggableBody!: ElementRef<HTMLDivElement>;
  @ViewChild('dragPointer', { static: true }) private dragpointer!: ElementRef<HTMLDivElement>;
  private document = inject(DOCUMENT);
  private _destroyRef = inject(DestroyRef);

  constructor(
    private rewardsWalletQuery: RewardsWalletQuery,
    private rewardsWalletService: RewardsWalletService,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    const rootStyles = getComputedStyle(document.body);
    this.maxTranslateY = toNumber(rootStyles.getPropertyValue('--wallet-max-translate-y'));
    firstValueFrom(this.rewardsWalletService.fetchWalletItems());
    this.walletItems$ = this.rewardsWalletQuery.selectGroupedUnredeemedWalletItems();
  }

  ngOnDestroy(): void {
    this.renderer.setStyle(this.document.body, 'overflow', 'auto');
    this.renderer.removeStyle(this.document.body, 'position');
    this.renderer.removeStyle(this.document.body, 'overscroll-behavior');
    this.renderer.removeStyle(this.document.body, 'touch-action');
  }

  ngAfterViewInit(): void {
    fromEvent(window, 'resize')
      .pipe(
        startWith(window.innerWidth < 992),
        map(() => window.innerWidth < 992),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((isMobile) => {
        if (isMobile) {
          this._setTranslateY(this.maxTranslateY);
          this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
          this.renderer.setStyle(this.document.body, 'position', `fixed`);
          this.renderer.setStyle(this.document.body, 'overscroll-behavior', `none`);
          this.renderer.setStyle(this.document.body, 'touch-action', `none`);
        } else {
          this._setTranslateY(0);
        }
      });
    this._initializeDragEvents();
  }

  private _initializeDragEvents(): void {
    const mouseDown$ = merge(
      fromEvent(this.dragpointer.nativeElement, 'mousedown'),
      fromEvent(this.draggable.nativeElement, 'touchstart'),
      fromEvent(this.dragpointer.nativeElement, 'touchstart')
    );
    const mouseMove$ = merge(
      fromEvent(this.draggable.nativeElement, 'touchmove'),
      fromEvent(this.dragpointer.nativeElement, 'touchmove'),
      fromEvent(window, 'mousemove')
    );
    const mouseUp$ = merge(
      fromEvent(this.draggable.nativeElement, 'touchend'),
      fromEvent(this.dragpointer.nativeElement, 'touchend'),
      fromEvent(window, 'mouseup')
    ).pipe(
      tap(() => {
        if (!this.isDragging) return;
        const halfway = (this.maxTranslateY + this.minTranslateY) / 2;
        // If draggable element is more than halfway down, animate to maxTranslateY, else animate to minTranslateY
        if (this.translateY > halfway) {
          gsap.fromTo(
            this.draggable.nativeElement,
            { transform: `translateY(${this.translateY}px)` },
            {
              transform: `translateY(${this.maxTranslateY}px)`,
              ease: 'power2.out',
              duration: 0.2,
              onComplete: () => {
                this.translateY = this.maxTranslateY;
                this.renderer.setStyle(this.draggableBody.nativeElement, 'overflow-y', `hidden`);
                gsap.to(this.draggableBody.nativeElement, {
                  scrollTop: 0,
                  duration: 0.2,
                  ease: 'power2.out',
                });
              },
            }
          );
        } else {
          gsap.fromTo(
            this.draggable.nativeElement,
            { transform: `translateY(${this.translateY}px)` },
            {
              transform: `translateY(${this.minTranslateY}px)`,
              ease: 'power2.out',
              duration: 0.2,
              onComplete: () => {
                this.translateY = this.minTranslateY;
                this.renderer.setStyle(this.draggableBody.nativeElement, 'overflow-y', `auto`);
              },
            }
          );
        }
        this.isDragging = false;
      })
    );

    const isScrollableAndScrolled = (target: HTMLElement): boolean => {
      const scrollEl = this.draggable.nativeElement;
      return scrollEl.contains(target) && scrollEl.scrollTop > 0;
    };

    mouseDown$
      .pipe(
        filter((start: any) => {
          const target = start.target as HTMLElement;
          const fromDragHandle = this.dragpointer.nativeElement.contains(target);
          const canDrag = this.translateY > this.minTranslateY || fromDragHandle;
          if (this.translateY === this.minTranslateY && isScrollableAndScrolled(target)) {
            return false;
          }

          return canDrag;
        }),
        exhaustMap((start: any) => {
          const startY = start instanceof TouchEvent ? start.touches[0].clientY : start.clientY;
          const startTranslateY = this.translateY;
          let dragged = false;
          let movedY = 0;

          return mouseMove$.pipe(
            map((move: any) => {
              move.preventDefault();
              this.isDragging = true;
              const moveY = move instanceof TouchEvent ? move.touches[0].clientY : move.clientY;
              movedY = moveY - startY;
              if (Math.abs(movedY) > 5) {
                this.isDragging = true;
                dragged = true;
              }
              return startTranslateY + movedY;
            }),
            takeUntil(
              mouseUp$.pipe(
                tap(() => {
                  if (dragged) {
                    start.preventDefault();
                  }
                })
              )
            )
          );
        })
      )
      .subscribe((dy) => {
        let deltaY = Math.min(Math.max(dy, this.minTranslateY), this.maxTranslateY);

        this._setTranslateY(deltaY);
      });
  }

  private _setTranslateY(y: number): void {
    this.translateY = y;
    this.renderer.setStyle(this.draggable.nativeElement, 'transform', `translateY(${y}px)`);
  }

  setWalletHeight($event: string) {
    if ($event === 'singleDiscount') {
      this.maxTranslateY = 425;
    } else if ($event === 'noDiscount') {
      this.maxTranslateY = 388;
    }
  }
}
